import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { JobPackCard } from "@/components/audits/JobPackCard";
import { JobPackModal } from "@/components/audits/JobPackModal";

export const JobPacksTab = ({ clientSlug, readOnly, onCount }) => {
  const [packs, setPacks] = useState([]);
  const [meta, setMeta] = useState({ categories: [], required: [] });
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [modal, setModal] = useState({ open: false, edit: null });

  const load = () => api.get("/job-packs", { params: readOnly ? {} : { client_slug: clientSlug } }).then((r) => setPacks(r.data)).catch(() => setPacks([]));
  useEffect(() => { if (clientSlug) load(); /* eslint-disable-next-line */ }, [clientSlug]);
  useEffect(() => { api.get("/job-packs/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { onCount?.(packs.length); }, [packs, onCount]);

  const replace = (p) => setPacks((list) => (list.some((x) => x.id === p.id) ? list.map((x) => (x.id === p.id ? p : x)) : [p, ...list]));
  const remove = async (p) => {
    if (!window.confirm(`Delete job pack ${p.job_number} and all its files?`)) return;
    try { await api.delete(`/job-packs/${p.id}`); setPacks((l) => l.filter((x) => x.id !== p.id)); toast.success("Job pack deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const shown = useMemo(() => packs.filter((p) => (!onlyMissing || !p.complete) &&
    (!q || `${p.job_number} ${p.customer} ${p.description}`.toLowerCase().includes(q.toLowerCase()))), [packs, q, onlyMissing]);
  const ready = packs.filter((p) => p.complete).length;

  return (
    <div data-testid="jp-tab" className="space-y-4">
      <div className="glass-card rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <PackageCheck className="h-5 w-5 text-indigo-600" />
        <div className="mr-auto">
          <div className="font-display font-bold text-[15px]">Third-Party Audit Prep</div>
          <div className="text-xs text-slate-500" data-testid="jp-summary">
            {readOnly ? "Job packs shared with you for this audit (read-only)." : `${ready} of ${packs.length} job packs audit ready · each needs ${meta.required.join(", ") || "Quote, Job Card, Invoice"}`}
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input data-testid="jp-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job, customer..." className="h-8 pl-8 w-[200px]" />
        </div>
        {!readOnly && <Button size="sm" variant={onlyMissing ? "default" : "outline"} className="h-8" onClick={() => setOnlyMissing((v) => !v)} data-testid="jp-missing-filter">Incomplete only</Button>}
        {!readOnly && <Button size="sm" className="h-8 brand-gradient text-white" onClick={() => setModal({ open: true, edit: null })} data-testid="jp-new-button"><Plus className="h-4 w-4 mr-1" /> New job pack</Button>}
      </div>
      {shown.length === 0 && (
        <div data-testid="jp-empty" className="glass-card rounded-2xl p-8 text-center text-sm text-slate-500">
          {packs.length ? "No job packs match." : readOnly ? "No job packs have been shared with you." : "No job packs yet. Create one per job and upload its quote, job card and invoice for your auditors."}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {shown.map((p) => (
          <JobPackCard key={p.id} pack={p} categories={meta.categories} readOnly={readOnly} onChanged={replace}
            onEdit={() => setModal({ open: true, edit: p })} onDelete={() => remove(p)} />
        ))}
      </div>
      <JobPackModal open={modal.open} edit={modal.edit} clientSlug={clientSlug} onClose={() => setModal({ open: false, edit: null })}
        onSaved={(p) => { replace(p); setModal({ open: false, edit: null }); }} />
    </div>
  );
};
